import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../hooks/useAuth';
import PageShell from '../components/PageShell';
import JsonViewer from '../components/JsonViewer';
const BatchJobPage = () => {
    const { jobId } = useParams();
    const { token } = useAuth();
    const [job, setJob] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [logs, setLogs] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState(null);
    // 認証トークンの設定
    useEffect(() => {
        if (token) {
            apiClient.setToken(token);
        }
    }, [token]);
    // バッチジョブの詳細取得
    const fetchBatchJob = async () => {
        if (!jobId) {
            setError('ジョブIDが指定されていません');
            return;
        }
        try {
            setIsLoading(true);
            const response = await apiClient.susugi.getBatchJob(jobId);
            if (response.success) {
                setJob(response.data);
                // ログの取得（jobにlog情報があれば表示）
                if (response.data && response.data.log) {
                    setLogs(response.data.log.split('\n'));
                }
            }
            else if (response.id) {
                // SusugiのAPIが直接データを返す場合
                const jobData = response;
                setJob(jobData);
                if (jobData.log) {
                    setLogs(jobData.log.split('\n'));
                }
            }
            else {
                setError(response.error || 'バッチジョブの取得に失敗しました');
            }
        }
        catch (err) {
            setError('ネットワークエラーが発生しました');
        }
        finally {
            setIsLoading(false);
        }
    };
    // 初期データの取得
    useEffect(() => {
        fetchBatchJob();
    }, [jobId]);
    // 自動更新の設定（実行中の場合のみ）
    useEffect(() => {
        if (job && job.status === 'running') {
            const interval = setInterval(() => {
                fetchBatchJob();
            }, 2000); // 2秒ごとに更新
            setRefreshInterval(interval);
            return () => {
                if (interval) {
                    clearInterval(interval);
                }
            };
        }
        else if (refreshInterval) {
            clearInterval(refreshInterval);
            setRefreshInterval(null);
        }
    }, [job?.status]);
    // バッチジョブの開始
    const startBatchJob = async () => {
        if (!job || !jobId)
            return;
        try {
            const response = await apiClient.susugi.startBatchJob(jobId);
            if (response.success) {
                fetchBatchJob();
                setError(null);
            }
            else {
                setError(response.error || 'バッチジョブの開始に失敗しました');
            }
        }
        catch (err) {
            setError('ネットワークエラーが発生しました');
        }
    };
    // バッチジョブの停止
    const stopBatchJob = async () => {
        if (!job || !jobId)
            return;
        try {
            const response = await apiClient.susugi.stopBatchJob(jobId);
            if (response.success) {
                fetchBatchJob();
                setError(null);
            }
            else {
                setError(response.error || 'バッチジョブの停止に失敗しました');
            }
        }
        catch (err) {
            setError('ネットワークエラーが発生しました');
        }
    };
    // 状態に応じた色の取得
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'running':
                return 'bg-blue-100 text-blue-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'failed':
                return 'bg-red-100 text-red-800';
            case 'cancelled':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };
    // 状態に応じた日本語表示
    const getStatusText = (status) => {
        switch (status) {
            case 'pending':
                return '待機中';
            case 'running':
                return '実行中';
            case 'completed':
                return '完了';
            case 'failed':
                return '失敗';
            case 'cancelled':
                return 'キャンセル';
            default:
                return '不明';
        }
    };
    // 処理時間の計算
    const getDuration = () => {
        if (!job || !job.started_at)
            return '-';
        const start = new Date(job.started_at);
        const end = job.completed_at ? new Date(job.completed_at) : new Date();
        const diff = end.getTime() - start.getTime();
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        if (hours > 0) {
            return `${hours}時間${minutes}分${seconds}秒`;
        }
        else {
            return `${minutes}分${seconds}秒`;
        }
    };
    // 進捗率の計算
    const getProgressPercentage = () => {
        if (!job)
            return 0;
        return Math.round(job.progress * 100);
    };
    if (isLoading) {
        return (_jsx("div", { className: "flex justify-center items-center h-64", children: _jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" }) }));
    }
    if (!job) {
        return (_jsx("div", { className: "text-center py-8", children: _jsx("p", { className: "text-gray-500", children: "\u30D0\u30C3\u30C1\u30B8\u30E7\u30D6\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093" }) }));
    }
    return (_jsx(PageShell, { title: job.id, subtitle: job.job_type, actions: _jsxs(_Fragment, { children: [job.status === 'pending' && (_jsx("button", { onClick: startBatchJob, className: "bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors", children: "\u958B\u59CB" })), job.status === 'running' && (_jsx("button", { onClick: stopBatchJob, className: "bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors", children: "\u505C\u6B62" })), _jsx("button", { onClick: fetchBatchJob, className: "bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors", children: "\u66F4\u65B0" })] }), children: _jsxs("div", { className: "space-y-6", children: [error && (_jsxs("div", { className: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative", children: [_jsx("span", { className: "block sm:inline", children: error }), _jsx("button", { onClick: () => setError(null), className: "absolute top-0 bottom-0 right-0 px-4 py-3", children: "\u00D7" })] })), _jsxs("div", { className: "bg-white rounded-lg shadow overflow-hidden", children: [_jsx("div", { className: "px-6 py-4 border-b border-gray-200", children: _jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "\u30B8\u30E7\u30D6\u60C5\u5831" }) }), _jsx("div", { className: "p-6", children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u72B6\u614B" }), _jsx("span", { className: `inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(job.status)}`, children: getStatusText(job.status) })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u30BF\u30A4\u30D7" }), _jsx("p", { className: "text-sm text-gray-900", children: job.job_type })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u4F5C\u6210\u65E5\u6642" }), _jsx("p", { className: "text-sm text-gray-900", children: new Date(job.created_at).toLocaleString('ja-JP') })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u66F4\u65B0\u65E5\u6642" }), _jsx("p", { className: "text-sm text-gray-900", children: new Date(job.updated_at).toLocaleString('ja-JP') })] }), job.started_at && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u958B\u59CB\u65E5\u6642" }), _jsx("p", { className: "text-sm text-gray-900", children: new Date(job.started_at).toLocaleString('ja-JP') })] })), job.completed_at && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u5B8C\u4E86\u65E5\u6642" }), _jsx("p", { className: "text-sm text-gray-900", children: new Date(job.completed_at).toLocaleString('ja-JP') })] })), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u51E6\u7406\u6642\u9593" }), _jsx("p", { className: "text-sm text-gray-900", children: getDuration() })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: "\u9032\u6357\u7387" }), _jsxs("p", { className: "text-sm text-gray-900", children: [getProgressPercentage(), "%"] })] })] }) })] }), _jsxs("div", { className: "bg-white rounded-lg shadow overflow-hidden", children: [_jsx("div", { className: "px-6 py-4 border-b border-gray-200", children: _jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "\u9032\u6357\u8A73\u7D30" }) }), _jsx("div", { className: "p-6", children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsxs("div", { className: "flex justify-between mb-2", children: [_jsx("span", { className: "text-sm font-medium text-gray-700", children: "\u5168\u4F53\u9032\u6357" }), _jsxs("span", { className: "text-sm text-gray-500", children: [getProgressPercentage(), "%"] })] }), _jsx("div", { className: "w-full bg-gray-200 rounded-full h-3", children: _jsx("div", { className: "bg-blue-600 h-3 rounded-full transition-all duration-300", style: { width: `${getProgressPercentage()}%` } }) })] }), _jsx("div", { className: "grid grid-cols-1 gap-4", children: _jsxs("div", { className: "text-center", children: [_jsxs("div", { className: "text-2xl font-bold text-blue-600", children: [getProgressPercentage(), "%"] }), _jsx("div", { className: "text-sm text-gray-500", children: "\u5B8C\u4E86" })] }) })] }) })] }), job.result && (_jsxs("div", { className: "bg-white rounded-lg shadow overflow-hidden", children: [_jsx("div", { className: "px-6 py-4 border-b border-gray-200", children: _jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "\u5B9F\u884C\u7D50\u679C" }) }), _jsx("div", { className: "p-6", children: _jsx(JsonViewer, { data: job.result }) })] })), job.error_message && (_jsxs("div", { className: "bg-white rounded-lg shadow overflow-hidden", children: [_jsx("div", { className: "px-6 py-4 border-b border-gray-200", children: _jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "\u30A8\u30E9\u30FC\u8A73\u7D30" }) }), _jsx("div", { className: "p-6", children: _jsx("div", { className: "bg-red-50 border border-red-200 rounded-md p-4", children: _jsx("p", { className: "text-sm text-red-700", children: job.error_message }) }) })] })), logs.length > 0 && (_jsxs("div", { className: "bg-white rounded-lg shadow overflow-hidden", children: [_jsx("div", { className: "px-6 py-4 border-b border-gray-200", children: _jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "\u5B9F\u884C\u30ED\u30B0" }) }), _jsx("div", { className: "p-6", children: _jsx("div", { className: "bg-gray-900 text-green-400 p-4 rounded-md font-mono text-sm overflow-auto max-h-96", children: logs.map((line, index) => (_jsx("div", { className: "mb-1", children: line }, index))) }) })] }))] }) }));
};
export default BatchJobPage;
